/**
 * @-Erwähnungen im Kommentartext auflösen – Spiegel der Backend-Logik in
 * backend/utils/mentions.py. Dient nur der Vorschau ("wer wird benachrichtigt?");
 * verschickt wird ausschließlich anhand der Server-Auswertung.
 */

const MENTION_RE = /(?:^|[^\w.@-])@([\w.-]+(?:[ \u00a0][\w.-]+){0,2})/gu;

// DOMParser ist inert: anders als innerHTML auf einem Element des Live-Dokuments
// lädt es keine Bilder und führt keine onerror-Handler aus.
const htmlToText = (html) => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ');
};

/** (key, user)-Paare -> Map; kollidierende Keys fallen raus. */
function indexPass(pairs) {
  const index = new Map();
  const ambiguous = new Set();
  pairs.forEach(([key, user]) => {
    const k = (key || '').trim().toLowerCase();
    if (!k || ambiguous.has(k)) return;
    const existing = index.get(k);
    if (existing && existing.id !== user.id) {
      index.delete(k);
      ambiguous.add(k);
      return;
    }
    index.set(k, user);
  });
  return index;
}

/** Vor- und Nachname einzeln: „@Martin" und „@Zmarsly" sollen beide ziehen. */
function nameParts(user) {
  if (!user.full_name) return [];
  const parts = user.full_name.trim().split(/\s+/);  // \s deckt auch NBSP ab
  return parts.length === 1 ? [parts[0]] : [parts[0], parts[parts.length - 1]];
}

/**
 * name (lowercase) -> user. Zwei Stufen: Username/voller Name haben Vorrang,
 * einzelne Vor-/Nachnamen füllen nur auf, was dort frei ist.
 * Spiegelt _candidate_index in backend/utils/mentions.py.
 */
function buildIndex(users) {
  const exact = indexPass([
    ...users.map((u) => [u.username, u]),
    ...users.map((u) => [u.full_name, u]),
  ]);
  const index = indexPass(users.flatMap((u) => nameParts(u).map((p) => [p, u])));
  exact.forEach((user, key) => index.set(key, user)); // exakte Treffer gewinnen
  return index;
}

/**
 * Findet die Erwähnung, die an dieser Fundstelle wirklich auflöst.
 * Liefert { user, length } – length ist die Zeichenzahl ab dem @, damit der
 * Aufrufer exakt den erkannten Teil markieren kann („@Martin Strand, hallo"
 * markiert den Namen, nicht das Komma).
 */
function matchAt(index, raw) {
  // Tokens und Trenner abwechselnd, damit die Originallänge rekonstruierbar ist
  const parts = raw.split(/([ \u00a0]+)/);
  const tokens = parts.filter((_, i) => i % 2 === 0);
  for (let size = tokens.length; size > 0; size -= 1) {
    const original = parts.slice(0, size * 2 - 1).join('');
    const candidate = tokens.slice(0, size).join(' ').toLowerCase();
    const exact = index.get(candidate);
    if (exact) return { user: exact, length: 1 + original.length };
    const trimmed = candidate.replace(/[.,;:!?]+$/, '');
    const user = trimmed === candidate ? null : index.get(trimmed);
    if (user) {
      return { user, length: 1 + original.length - (candidate.length - trimmed.length) };
    }
  }
  return null;
}

/**
 * Markiert im gerenderten Kommentar-HTML die Erwähnungen, die tatsächlich
 * aufgelöst haben, mit <span class="rotis-mention">. Nicht erkannte @Namen
 * bleiben unverändert – genau daran sieht man, dass sie ins Leere liefen.
 *
 * Arbeitet nur auf Textknoten, Tags und Attribute werden nicht angefasst.
 */
export function highlightMentions(html, users) {
  if (!html || !users || users.length === 0 || !html.includes('@')) return html;
  const index = buildIndex(users);
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstChild;
  if (!root) return html;

  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeValue && n.nodeValue.includes('@')) targets.push(n);
  }

  let touched = false;
  targets.forEach((node) => {
    const text = node.nodeValue;
    const frag = doc.createDocumentFragment();
    let cursor = 0;
    MENTION_RE.lastIndex = 0;
    for (let m = MENTION_RE.exec(text); m; m = MENTION_RE.exec(text)) {
      const at = m.index + m[0].indexOf('@');
      const hit = matchAt(index, m[1]);
      if (!hit) continue;
      frag.appendChild(doc.createTextNode(text.slice(cursor, at)));
      const span = doc.createElement('span');
      span.className = 'rotis-mention';
      span.textContent = text.slice(at, at + hit.length);
      span.setAttribute('title', `${hit.user.full_name || hit.user.username} wurde erwähnt`);
      frag.appendChild(span);
      cursor = at + hit.length;
      touched = true;
    }
    if (cursor === 0) return;
    frag.appendChild(doc.createTextNode(text.slice(cursor)));
    node.parentNode.replaceChild(frag, node);
  });

  return touched ? root.innerHTML : html;
}

/** Alle im Text erwähnten User (längster Treffer gewinnt, ohne Dubletten). */
export function resolveMentions(html, users) {
  const text = htmlToText(html);
  if (!text.includes('@') || !users || users.length === 0) return [];

  const index = buildIndex(users);
  const found = [];
  const seen = new Set();

  MENTION_RE.lastIndex = 0;
  let match = MENTION_RE.exec(text);
  while (match) {
    const tokens = match[1].trim().split(/[ \u00a0]+/);
    for (let size = tokens.length; size > 0; size -= 1) {
      const candidate = tokens.slice(0, size).join(' ').toLowerCase();
      const user = index.get(candidate) || index.get(candidate.replace(/[.,;:!?]+$/, ''));
      if (user) {
        if (!seen.has(user.id)) { seen.add(user.id); found.push(user); }
        break;
      }
    }
    match = MENTION_RE.exec(text);
  }
  return found;
}

export default resolveMentions;
